import { AxiosError } from 'axios';
import { api } from '@/Data/api';
import ss from '../Session';
import { SocialResponse } from './types';
import type { FeedResponse, GetFeedParams } from 'ai-api/types/social';

// Re-export advanced feed types from ai-api
export type {
    FeedType,
    ScoreVector,
    FeedConfig,
    GetFeedParams,
    FeedMetadata,
    FeedResponse
} from 'ai-api/types/social';

/**
 * Get advanced personalized feeds with dense feature vectors and metadata
 * Uses three-stage pipeline: Retriever → Ranker → Balancer
 * @param params GetFeedParams with type, config, limit, offset, grouped
 * @returns Promise with FeedResponse containing posts and metadata (and groups if grouped=true)
 */
export const getFeed = async (params: GetFeedParams & { grouped?: boolean } = {}): Promise<FeedResponse | null> => {
    try {
        const queryParams: Record<string, any> = { ...params };
        if (params.grouped) { queryParams.grouped = ''; }

        const res = await api.get<SocialResponse<FeedResponse>>('/social/feed', {
            params: queryParams,
            headers: {
                Authorization: `Bearer ${ss.getCurrentSession().jwt}`
            }
        });

        return res.data?.code === 200 ? res.data.data ?? null : null;
    } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.code === 404) {
            console.warn('No feed content found for the given parameters');
            return {
                posts: [],
                metadata: {
                    retriever_stats: { personal: 0, demographic: 0, global: 0 },
                    ranking_info: { total_candidates: 0, avg_score: 0, score_range: [0, 0] },
                    diversity_metrics: { 
                        unique_authors: 0,
                        multi_retriever_posts: 0, 
                        source_distribution: { personal: 0, demographic: 0, global: 0 }
                    },
                    score_vector_sample: null
                }
            };
        }
        
        console.error('Failed to get feed:', e);
        return null;
    }
};